import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { attendanceRulesApi } from '../api/attendanceRulesApi';
import { appToast } from '../../../../components/AppToast';

const DEFAULT_FORM_STATE = {
  ruleName: '',
  alias: '',
  swipeTimeAfter: 0,
  swipeTimeBefore: 0,
  lateHalfDayDeduct: 0,
  earlyHalfDayDeduct: 0,
  noOfLateEarlyMarks: 0,
  lateEarlyDaysDeduct: 0,
  sandwitchPresent: false,
};

export function useAttendanceRulesEntry() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('id');
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      attendanceRulesApi
        .getRecordById(editId)
        .then((record) => {
          if (record) {
            setForm({
              ruleName: record.ruleName || '',
              alias: record.alias || '',
              swipeTimeAfter: record.swipeTimeAfter ?? 0,
              swipeTimeBefore: record.swipeTimeBefore ?? 0,
              lateHalfDayDeduct: record.lateHalfDayDeduct ?? 0,
              earlyHalfDayDeduct: record.earlyHalfDayDeduct ?? 0,
              noOfLateEarlyMarks: record.noOfLateEarlyMarks ?? 0,
              lateEarlyDaysDeduct: record.lateEarlyDaysDeduct ?? 0,
              sandwitchPresent: Boolean(record.sandwitchPresent),
            });
          } else {
            appToast.error('Attendance rule not found.');
            navigate('/payroll/attendance-rules');
          }
        })
        .catch(() => {
          appToast.error('Failed to load attendance rule details.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [editId, isEdit, navigate]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    if (!form.ruleName.trim()) {
      appToast.error('Rule Name is required.');
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        await attendanceRulesApi.updateRecord(editId, form);
        appToast.success('Attendance Rule updated successfully!');
      } else {
        await attendanceRulesApi.createRecord(form);
        appToast.success('Attendance Rule created successfully!');
      }

      if (exitAfter) {
        navigate('/payroll/attendance-rules');
      } else if (!isEdit) {
        setForm(DEFAULT_FORM_STATE);
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      appToast.error(typeof msg === 'string' ? msg : 'Failed to save Attendance Rule.');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      isEdit,
      editId,
      isLoading,
      isSaving,
    },
    actions: {
      patchForm,
      handleSave,
    },
  };
}
